import { useCallback, useContext, useMemo } from 'react';
import { ace_hooks, assignment_hooks } from '@axo/shared/data-access/hooks';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import { assignment } from '@axo/shared/data-access/types';
import { MarketCountryCode } from '@axo/shared/data-access/types/ace_index';
import { useShouldUseAssignmentsVersion } from './useShouldUseAssignmentsVersion';
import { useSetAgentAvailabilityV3 } from './useSetAgentAvailabilityV3';

type Versions = 'v3' | 'buckets' | 'legacy';

type UseAgentAvailabilityParams = {
  marketCountry: string;
};

type PortfolioStats = {
  assignedApplicationCount: number;
  unprocessedStats: assignment.UnprocessedCount | undefined;
};

type UseAgentAvailabilityResult = {
  isAvailable: boolean;
  toggleAvailability: () => Promise<void>;
  isToggleLoading: boolean;
  portfolioStats: PortfolioStats | undefined;
  version: Versions;
};

export const useAgentAvailability = ({
  marketCountry,
}: UseAgentAvailabilityParams): UseAgentAvailabilityResult => {
  const version = useShouldUseAssignmentsVersion({ marketCountry });
  const isV3 = version === 'v3';
  const isBuckets = version === 'buckets';
  const isLegacy = version === 'legacy';

  const user = useContext(DataAccessContext).state.user;

  // V3
  const agentAssignment = ace_hooks.useGetAgentAssignmentV3(user.clientID, {
    enabled: isV3,
  });
  const { setAgentAvailabilityV3, isLoading: isAgentAvailabilityLoading } =
    useSetAgentAvailabilityV3(user.clientID);
  const agentAssignmentsList = ace_hooks.useGetAgentAssignmentsListV3(
    user.clientID,
    { enabled: isV3 }
  );

  // Buckets
  const { data: assigneeData, isLoading: isLoadingAssigneeData } =
    ace_hooks.useAssignee(user.clientID);
  const patchAssignee = ace_hooks.usePatchAssignee();

  // Legacy
  const assignee = assignment_hooks.useAssignee(marketCountry, {
    enabled: isLegacy || isBuckets,
  });
  const updateAvailability =
    assignment_hooks.useUpdateAvailability(marketCountry);
  const unprocessedStats = assignment_hooks.useCountUnprocessed(marketCountry, {
    enabled: isLegacy || isBuckets,
  });
  const assignedApplications = assignment_hooks.useAssignedApplications(
    marketCountry,
    { enabled: isLegacy || isBuckets }
  );

  const isAvailable = useMemo(() => {
    if (isV3) return agentAssignment.data?.Available ?? false;
    if (isBuckets) return assigneeData?.AvailableInCountry === marketCountry;
    return (
      assignee.data?.AvailabilityStatus ===
      assignment.AvailabilityStatus.Available
    );
  }, [
    isV3,
    isBuckets,
    agentAssignment.data?.Available,
    assigneeData?.AvailableInCountry,
    marketCountry,
    assignee.data?.AvailabilityStatus,
  ]);

  const toggleAvailability = useCallback(async () => {
    if (isV3) {
      await setAgentAvailabilityV3(!isAvailable);
    } else if (isBuckets) {
      await patchAssignee.mutateAsync({
        clientID: user.clientID,
        marketCountry: isAvailable ? '' : (marketCountry as MarketCountryCode),
      });
    } else {
      await updateAvailability.mutateAsync(
        isAvailable
          ? assignment.AvailabilityStatus.Unavailable
          : assignment.AvailabilityStatus.Available
      );
    }
  }, [
    isV3,
    isBuckets,
    isAvailable,
    setAgentAvailabilityV3,
    patchAssignee,
    user.clientID,
    marketCountry,
    updateAvailability,
  ]);

  const isToggleLoading = isV3
    ? agentAssignment.isLoading || isAgentAvailabilityLoading
    : isBuckets
    ? isLoadingAssigneeData || patchAssignee.isLoading
    : assignee.isLoading || updateAvailability.isLoading;

  const portfolioStats: PortfolioStats | undefined = useMemo(() => {
    if (isV3) {
      const assignments = agentAssignmentsList.data ?? [];
      const unprocessedCount = assignments.filter((a) => !a.ProcessedAt).length;
      return {
        assignedApplicationCount: assignments.length,
        unprocessedStats: {
          NumActive: unprocessedCount,
          NumQueue: 0,
        },
      };
    }
    if (isLegacy) {
      return {
        assignedApplicationCount: assignedApplications.data?.Items.length ?? 0,
        unprocessedStats: unprocessedStats.data,
      };
    }
    return undefined;
  }, [
    isV3,
    isLegacy,
    agentAssignmentsList.data,
    assignedApplications.data?.Items.length,
    unprocessedStats.data,
  ]);

  return {
    isAvailable,
    toggleAvailability,
    isToggleLoading,
    portfolioStats,
    version,
  };
};
